// Import react elements
import React from "react";

// Import components
import ToggleSwitch from "global/components/ToggleSwitch";

// Import utilities
import {
    buildProductColourwayPayload,
    getAvailableProductColourways,
    getSelectedProductColourwayCodes,
    getDefaultProductColourwayCode,
    normaliseDefaultColourway,
    normaliseColourwayCodes,
    productUsesAllColourways
} from "../utilities/PodProductColourwayHelpers";



// Render one POD product colourway selector
const ProductColourwaySelector = ({
    canonicalProduct,
    product,
    disabled = false,
    allowInheritedDefault = false,
    parentDefaultColourwayCode = "",
    parentMockupColourways = [],
    disabledDefaultColourwayCodes = [],
    disabledMockupColourwayCodes = [],
    onChange
}) => {

    // Get the available colourways from the canonical POD product
    const availableColourways = getAvailableProductColourways(canonicalProduct);

    // If the canonical POD product has no colourways, do not show this control
    if (availableColourways.length === 0) return null;

    // Get the available colourway codes
    const availableColourwayCodes = normaliseColourwayCodes(availableColourways);

    // Check whether this product is deriving all colourways from the canonical POD product
    const useAllColourways = productUsesAllColourways(product);

    // Get the selected colourway codes stored on the design/template product
    const storedSelectedColourwayCodes = getSelectedProductColourwayCodes(product);

    // If using all colourways, display all available colourways as selected
    const selectedColourwayCodes = useAllColourways ? availableColourwayCodes : storedSelectedColourwayCodes;

    // Read whether this child inherits the effective configured parent default
    const defaultColourwayInherited = allowInheritedDefault && String(product?.default_colourway_inherited || "N").trim().toUpperCase() === "Y";

    // Normalise the configured parent default code
    const safeParentDefaultColourwayCode = String(parentDefaultColourwayCode || "").trim();

    // Get the matching parent default colourway details for the inheritance label
    const parentDefaultColourway = availableColourways.find((colourway) => String(colourway?.code || "").trim() === safeParentDefaultColourwayCode) || null;

    // Get the default colourway code stored on the design/template product
    const storedDefaultColourwayCode = getDefaultProductColourwayCode(product);

    // Inherited children use the live configured parent default; other products validate their own effective value
    const defaultColourwayCode = defaultColourwayInherited
        ? safeParentDefaultColourwayCode
        : (useAllColourways
            ? normaliseDefaultColourway(availableColourwayCodes, storedDefaultColourwayCode)
            : normaliseDefaultColourway(selectedColourwayCodes, storedDefaultColourwayCode));

    // Read whether this product has a deliberately selected mockup colourway list
    const mockupColourwaysExplicit =
        !useAllColourways &&
        String(product?.mockup_colourways_explicit || "N").trim().toUpperCase() === "Y";

    // Normalise the configured parent's effective mockup colourways for child inheritance
    const parentMockupColourwayCodes = normaliseColourwayCodes(parentMockupColourways);

    // Automatic children inherit mockup colourways while their default also comes from the parent
    const mockupColourwaysInherited =
        allowInheritedDefault &&
        defaultColourwayInherited &&
        !mockupColourwaysExplicit;

    // Start with the effective mockup colourways for this product
    let mockupColourwayCodes = [];

    if (mockupColourwaysExplicit) {

        // Keep only mockup colourways which are still enabled on this product
        mockupColourwayCodes = normaliseColourwayCodes(product?.mockup_colourways)
            .filter((colourwayCode) => selectedColourwayCodes.includes(colourwayCode));

        // The effective default always comes first in an explicit mockup selection
        if (defaultColourwayCode && selectedColourwayCodes.includes(defaultColourwayCode)) {
            mockupColourwayCodes = [
                defaultColourwayCode,
                ...mockupColourwayCodes.filter((colourwayCode) => colourwayCode !== defaultColourwayCode),
            ];
        }

    } else if (mockupColourwaysInherited) {

        // Inherited children display the configured parent's complete compatible mockup selection
        mockupColourwayCodes = parentMockupColourwayCodes
            .filter((colourwayCode) => selectedColourwayCodes.includes(colourwayCode));

    } else if (defaultColourwayCode) {

        // Automatic parents and children with their own default generate only that default colourway
        mockupColourwayCodes = [defaultColourwayCode];

    }

    // Build lookups of parent selections unavailable on one or more configured children
    const disabledDefaultColourwayLookup = new Set(normaliseColourwayCodes(disabledDefaultColourwayCodes));
    const disabledMockupColourwayLookup = new Set(normaliseColourwayCodes(disabledMockupColourwayCodes));

    // Check whether this child can currently inherit its configured parent default
    const canInheritDefaultColourway = safeParentDefaultColourwayCode && selectedColourwayCodes.includes(safeParentDefaultColourwayCode);

    // Emit colourway, default and mockup-colourway state back to the parent component
    const emitColourwayChange = (
        nextSelectedColourwayCodes,
        nextDefaultColourwayCode,
        nextDefaultColourwayInherited = defaultColourwayInherited,
        nextMockupColourwayCodes = mockupColourwayCodes,
        nextMockupColourwaysExplicit = mockupColourwaysExplicit
    ) => {

        // Build the normal product-colourway fields expected by the API
        const payload = buildProductColourwayPayload(
            nextSelectedColourwayCodes,
            nextDefaultColourwayCode,
            availableColourways,
            nextDefaultColourwayInherited ? "Y" : "N"
        );

        // Read the effective enabled colourways after normalisation
        const effectiveSelectedColourwayCodes =
            payload.colourways === null
                ? availableColourwayCodes
                : normaliseColourwayCodes(payload.colourways);

        // Use all keeps mockup colourways in automatic mode
        if (payload.colourways === null) {
            nextMockupColourwaysExplicit = false;
        }

        // Automatic inherited children follow the configured parent's mockup selection
        if (
            nextDefaultColourwayInherited &&
            !nextMockupColourwaysExplicit
        ) {

            payload.mockup_colourways = parentMockupColourwayCodes
                .filter((colourwayCode) => effectiveSelectedColourwayCodes.includes(colourwayCode));
            payload.mockup_colourways_explicit = "N";
            payload.mockup_colourways_inherited = "Y";

        } else if (nextMockupColourwaysExplicit) {

            // Keep only enabled explicit mockup colourways and always put the default first
            let explicitMockupColourwayCodes = normaliseColourwayCodes(nextMockupColourwayCodes)
                .filter((colourwayCode) => effectiveSelectedColourwayCodes.includes(colourwayCode));

            explicitMockupColourwayCodes = explicitMockupColourwayCodes
                .filter((colourwayCode) => colourwayCode !== payload.default_colourway);

            if (payload.default_colourway) {
                explicitMockupColourwayCodes.unshift(payload.default_colourway);
            }

            // A list containing only the default is equivalent to automatic mode
            if (explicitMockupColourwayCodes.length <= 1) {
                payload.mockup_colourways = payload.default_colourway ? [payload.default_colourway] : [];
                payload.mockup_colourways_explicit = "N";
                payload.mockup_colourways_inherited = "N";
            } else {
                payload.mockup_colourways = explicitMockupColourwayCodes;
                payload.mockup_colourways_explicit = "Y";
                payload.mockup_colourways_inherited = "N";
            }

        } else {

            // Automatic non-inherited products generate only their effective default colourway
            payload.mockup_colourways = payload.default_colourway ? [payload.default_colourway] : [];
            payload.mockup_colourways_explicit = "N";
            payload.mockup_colourways_inherited = "N";

        }

        // Send the complete normalised colourway state back to the parent component
        onChange?.(payload);

    };

    // Toggle whether this product derives all colourways from the canonical POD product
    const handleUseAllColourwaysChange = (event) => {

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Get the boolean value from the toggle
        const nextUseAllColourways = event.target.value;

        // If the user is switching use all on, send null so the API derives from the canonical POD product
        if (nextUseAllColourways) {

            // Keep the effective inherited or explicit default while changing to derived colourways
            const nextDefaultColourwayCode = defaultColourwayInherited
                ? safeParentDefaultColourwayCode
                : normaliseDefaultColourway(availableColourwayCodes, defaultColourwayCode);

            // Emit null selected colourways and return mockups to automatic mode
            emitColourwayChange(
                null,
                nextDefaultColourwayCode,
                defaultColourwayInherited,
                [],
                false
            );

            // Stop here
            return;

        }

        // If the user is switching use all off, start with all canonical colourways and automatic mockups
        emitColourwayChange(
            availableColourwayCodes,
            defaultColourwayCode,
            defaultColourwayInherited,
            [],
            false
        );

    };

    // Toggle one colourway as selected or unselected
    const handleToggleColourway = (colourwayCode, event) => {

        // Stop the toggle click also selecting the default colourway
        event.stopPropagation();

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Do nothing if all colourways are being derived from the canonical POD product
        if (useAllColourways) return;

        // Check whether this colourway is currently selected
        const isSelected = selectedColourwayCodes.includes(colourwayCode);

        // Every child must retain the configured parent default in its available colourways
        if (allowInheritedDefault && isSelected && colourwayCode === safeParentDefaultColourwayCode) return;

        // An inherited parent mockup colourway cannot be disabled on the child
        if (
            mockupColourwaysInherited &&
            isSelected &&
            parentMockupColourwayCodes.includes(colourwayCode)
        ) return;

        // Create the next selected colourway list
        let nextSelectedColourwayCodes = [];

        // If the colourway is selected, remove it
        if (isSelected) {

            // Do not allow the user to remove the final selected colourway
            if (selectedColourwayCodes.length <= 1) return;

            // Remove the colourway from the selected list
            nextSelectedColourwayCodes = selectedColourwayCodes.filter((selectedColourwayCode) => selectedColourwayCode !== colourwayCode);

        } else {

            // Add the colourway to the selected list
            nextSelectedColourwayCodes = [
                ...selectedColourwayCodes,
                colourwayCode
            ];

        }

        // Make sure the default colourway is still valid after the selected list changed
        const nextDefaultColourwayCode = defaultColourwayInherited
            ? safeParentDefaultColourwayCode
            : normaliseDefaultColourway(nextSelectedColourwayCodes, defaultColourwayCode);

        // Remove disabled product colourways from the mockup selection at the same time
        const nextMockupColourwayCodes = mockupColourwayCodes
            .filter((mockupColourwayCode) => nextSelectedColourwayCodes.includes(mockupColourwayCode));

        // Emit the updated colourway and mockup selection
        emitColourwayChange(
            nextSelectedColourwayCodes,
            nextDefaultColourwayCode,
            defaultColourwayInherited,
            nextMockupColourwayCodes,
            mockupColourwaysExplicit
        );

    };

    // Set the default colourway
    const handleSetDefaultColourway = (colourwayCode) => {

        // Do nothing if the selector is disabled or this child inherits its default
        if (disabled || defaultColourwayInherited) return;

        // Do not allow a parent default that is unavailable on one or more children
        if (disabledDefaultColourwayLookup.has(colourwayCode)) return;

        // If using explicit colourways, do not allow an unselected colourway to become the default
        if (!useAllColourways && !selectedColourwayCodes.includes(colourwayCode)) return;

        // Emit the current colourway mode with the new default and keep it first in any explicit mockup list
        emitColourwayChange(
            useAllColourways ? null : selectedColourwayCodes,
            colourwayCode,
            false,
            mockupColourwayCodes,
            mockupColourwaysExplicit
        );;

    };

    // Toggle whether this child inherits the configured parent default colourway
    const handleInheritedDefaultChange = (event) => {

        // Do nothing if the selector is disabled
        if (disabled) return;

        // Get the boolean value from the toggle
        const nextDefaultColourwayInherited = event.target.value;

        // Do not enable inheritance unless the configured parent default is available on this child
        if (nextDefaultColourwayInherited && !canInheritDefaultColourway) return;

        // Inherited children use the live parent code; explicit children retain the current effective code
        const nextDefaultColourwayCode = nextDefaultColourwayInherited
            ? safeParentDefaultColourwayCode
            : defaultColourwayCode;

        // Switching inheritance also returns mockup colourways to automatic parent/default behaviour
        emitColourwayChange(
            useAllColourways ? null : selectedColourwayCodes,
            nextDefaultColourwayCode,
            nextDefaultColourwayInherited,
            [],
            false
        );

    };

        // Toggle whether one enabled non-default colourway should also generate mockups
    const handleToggleMockupColourway = (colourwayCode, event) => {

        // Keep the browser context menu out of the selector
        event.preventDefault();
        event.stopPropagation();

        // Mockup customisation is unavailable while disabled, using all colourways or inheriting the parent selection
        if (disabled || useAllColourways || mockupColourwaysInherited) return;

        // Mockups can only be enabled for colourways which are enabled on the product
        if (!selectedColourwayCodes.includes(colourwayCode)) return;

        // The default colourway always has a mockup and cannot be turned off
        if (colourwayCode === defaultColourwayCode) return;

        // Check whether this colourway currently has mockups enabled
        const mockupEnabled = mockupColourwayCodes.includes(colourwayCode);

        // Do not enable a parent mockup colourway which an inheriting child cannot use
        if (!mockupEnabled && disabledMockupColourwayLookup.has(colourwayCode)) return;

        // Toggle this colourway in the explicit mockup selection
        let nextMockupColourwayCodes = mockupColourwayCodes
            .filter((mockupColourwayCode) => mockupColourwayCode !== colourwayCode);

        if (!mockupEnabled) {
            nextMockupColourwayCodes.push(colourwayCode);
        }

        // Any non-default mockup colour makes the selection explicit; returning to default-only returns to automatic mode
        const nextMockupColourwaysExplicit = nextMockupColourwayCodes
            .some((mockupColourwayCode) => mockupColourwayCode !== defaultColourwayCode);

        // Emit the updated mockup selection without changing normal colourway selection or default
        emitColourwayChange(
            selectedColourwayCodes,
            defaultColourwayCode,
            defaultColourwayInherited,
            nextMockupColourwayCodes,
            nextMockupColourwaysExplicit
        );

    };

    return (

        <div className="pod-product-colourways">

            <div className="pod-product-colourways__header">

                <div className="pod-product-colourways__header-left">

                    <p className="pod-product-colourways__title">Selected colourways</p>

                   
                </div>

                <div className="pod-product-colourways__header-right">

                    {allowInheritedDefault &&

                        <ToggleSwitch
                            name={"inherit_default_colourway_" + (product?.pod_product_sku || "product")}
                            value={defaultColourwayInherited}
                            onChange={handleInheritedDefaultChange}
                            label={parentDefaultColourway?.name ? "Use parent default (" + parentDefaultColourway.name + ")" : "Use parent default"}
                            labelPosition="left"
                            disabled={disabled || (!defaultColourwayInherited && !canInheritDefaultColourway)}
                            title={canInheritDefaultColourway ? "Use the configured parent default colourway" : "The configured parent default is not available on this child"}
                            size="s"
                        />

                    }

                    <ToggleSwitch
                        name={"use_all_colourways_" + (product?.pod_product_sku || "product")}
                        value={useAllColourways}
                        onChange={handleUseAllColourwaysChange}
                        label="Use all"
                        labelPosition="left"
                        disabled={disabled}
                        size="s"
                    />

                </div>

            </div>

             <p className="pod-product-colourways__hint">
                Two green ticks include the colourway and mockup; one tick includes the colourway only. Use all follows the canonical POD product automatically and generates the automatic default mockup only. Turn it off to customise colourways and mockups. Click a colourway name or swatch to set the default. Right-click a tick to add or remove mockups.
            </p>


            <div className="pod-product-colourways__list">

                {availableColourways.map((colourway) => {

                    // Get the colourway code
                    const colourwayCode = String(colourway.code || "").trim();

                    // Check whether this colourway is selected
                    const isSelected = selectedColourwayCodes.includes(colourwayCode);

                    // Check whether this colourway is the default
                    const isDefault = defaultColourwayCode === colourwayCode;

                    // Two ticks mean colourway and mockup enabled; one tick means colourway only
                    const mockupEnabled = isSelected && mockupColourwayCodes.includes(colourwayCode);

                    // Check whether this colourway is unavailable as a parent default
                    const defaultSelectionDisabled = disabledDefaultColourwayLookup.has(colourwayCode);

                    // Check whether this child colourway is required by its configured parent default
                    const colourwayRequiredByParent = allowInheritedDefault && isSelected && colourwayCode === safeParentDefaultColourwayCode;

                    // Inherited parent mockups also require those colourways to remain enabled on the child
                    const colourwayRequiredByParentMockup =
                        mockupColourwaysInherited &&
                        isSelected &&
                        parentMockupColourwayCodes.includes(colourwayCode);

                    // Check whether this parent colourway cannot be added to mockups because an inheriting child cannot use it
                    const mockupSelectionUnavailable =
                        !mockupEnabled &&
                        disabledMockupColourwayLookup.has(colourwayCode);

                    // Build the tick tooltip for normal and mockup selection behaviour
                    const toggleTitle =
                        colourwayRequiredByParentMockup
                            ? "This colourway is required by the configured parent mockup selection"
                            : colourwayRequiredByParent
                                ? "This colourway is required by the configured parent default"
                                : !isSelected
                                    ? "Add colourway"
                                    : useAllColourways
                                        ? (mockupEnabled ? "Default mockup included automatically. Turn off Use all to customise mockups" : "Colourway enabled automatically. Turn off Use all to customise mockups")
                                        : mockupColourwaysInherited
                                            ? (mockupEnabled ? "Mockup included by the configured parent" : "Colourway enabled; mockup selection is inherited from the configured parent")
                                            : isDefault
                                                ? "Default colourway; its mockup is always included"
                                                : mockupSelectionUnavailable
                                                    ? "This mockup colourway is not available on every inheriting child product"
                                                    : mockupEnabled
                                                        ? "Colourway and mockup enabled. Left-click removes the colourway; right-click removes the mockup"
                                                        : "Colourway enabled without mockup. Left-click removes the colourway; right-click adds the mockup";

                    // Return the colourway row
                    return (

                        <div
                            key={colourwayCode}
                            className={
                                "pod-product-colourways__item" +
                                (isSelected ? " pod-product-colourways__item--selected" : " pod-product-colourways__item--unselected") +
                                (isDefault ? " pod-product-colourways__item--default" : "") +
                                (disabled ? " pod-product-colourways__item--disabled" : "")
                            }
                            title={colourway.name}
                        >

                            <button
                                type="button"
                                className={
                                    "pod-product-colourways__toggle" +
                                    (!isSelected
                                        ? " pod-product-colourways__toggle--unselected"
                                        : mockupEnabled
                                            ? " pod-product-colourways__toggle--mockup"
                                            : " pod-product-colourways__toggle--selected")
                                }
                                onClick={(event) => handleToggleColourway(colourwayCode, event)}
                                onContextMenu={(event) => handleToggleMockupColourway(colourwayCode, event)}
                                disabled={disabled || colourwayRequiredByParent || colourwayRequiredByParentMockup}
                                title={toggleTitle}
                            >

                                {!isSelected ? "✖" : (mockupEnabled ? "✔✔" : "✔")}

                            </button>

                            <button
                                type="button"
                                className={
                                    "pod-product-colourways__default-area" +
                                    (defaultSelectionDisabled ? " pod-product-colourways__default-area--unavailable" : "")
                                }
                                onClick={() => handleSetDefaultColourway(colourwayCode)}
                                disabled={disabled || !isSelected || defaultColourwayInherited || defaultSelectionDisabled}
                                title={
                                    defaultSelectionDisabled
                                        ? "This colourway is not available on every child product"
                                        : defaultColourwayInherited
                                            ? "This child is using the configured parent default"
                                            : isSelected
                                                ? "Set as default colourway"
                                                : "Select this colourway before setting it as default"
                                }
                            >

                                <span
                                    className="pod-product-colourways__swatch"
                                    style={{ backgroundColor: colourway.hex_colour || "transparent" }}
                                ></span>

                                <span className="pod-product-colourways__name">{colourway.name}</span>

                            </button>

                        </div>

                    );

                })}

            </div>

        </div>

    );

};



export default ProductColourwaySelector;